import type { ProductData } from '../components/ProductCreationWizard/ProductCreationWizard'

/**
 * Generate a hash from a URL string
 * Used for URL-based images/videos that don't have a server-generated hash
 */
function generateHashFromUrl(url: string): string {
  let hash = 0
  for (let i = 0; i < url.length; i++) {
    const char = url.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0')
}

/**
 * Transform basic product images to API photos format
 * Supports two modes:
 * - file_id mode: for server-uploaded files
 * - file_url + file_hash mode: for URL-based images
 */
export function transformImagesToPhotos(
  productImages: ProductData['basicInfo']['productImages']
): any[] {
  if (!productImages) return []

  return productImages
    .filter(img =>
      // Include successfully uploaded files with file_id
      (img.file_id && img.upload_status === 'success') ||
      // Or URL-based images that have a URL but no file_id
      (img.url && !img.file_id)
    )
    .map((img) => {
      if (img.file_id) {
        // Server-uploaded file - use file_id format
        return {
          file_id: img.file_id,
          is_primary: img.isMain || false,
          is_active: true
        }
      } else {
        // URL-based image - use file_url format with required file_hash
        return {
          file_url: img.url,
          file_hash: img.file_hash || generateHashFromUrl(img.url || ''),
          is_primary: img.isMain || false,
          is_active: true
        }
      }
    })
}

/**
 * Transform variant combinations from wizard format to API format
 */
export function transformVariantCombinations(
  salesInfo: ProductData['salesInfo'],
  shipping: ProductData['shipping']
): any[] {
  if (!salesInfo.hasVariants || !salesInfo.variantCombinations?.length) {
    return []
  }

  return salesInfo.variantCombinations.map((variant, index) => {
    // Build tier_variation array properly
    const tierVariation = variant.options && variant.optionValues 
      ? variant.options.map((optionName, idx) => ({
          [optionName]: variant.optionValues[idx] || ''
        }))
      : []

    return {
      sku: variant.sku || '',
      is_main_variant: variant.isMainVariant || index === 0,
      is_active: variant.isActive ?? true,
      stock: variant.stock || 0,
      tier_index: variant.tierIndex || [],
      tier_variation: tierVariation,
      weight: shipping.packageDimensions?.weight || 0,
      dimension: {
        height: shipping.packageDimensions?.height || 0,
        length: shipping.packageDimensions?.length || 0,
        width: shipping.packageDimensions?.width || 0
      },
      price: variant.price || 0,
      // Add cost price if available in the variant data
      ...(variant.cost !== undefined && { cost_price: variant.cost })
    }
  })
}

/**
 * Transform variant options from wizard format to API format
 */
export function transformVariantOptions(
  salesInfo: ProductData['salesInfo']
): any[] {
  if (!salesInfo.hasVariants || !salesInfo.variantOptions?.length) {
    return []
  }

  return salesInfo.variantOptions.map((option, index) => ({
    name: option.name,
    index: index,
    option_list: option.values?.map(value => ({
      option: value.name,
      image: value.image || ''
    })) || []
  }))
}

/**
 * Transform shipping companies to API shippers format
 */
export function transformShippingCompanies(
  shippingCompanies: ProductData['shipping']['shippingCompanies']
): any[] {
  if (!shippingCompanies) return []
  
  return shippingCompanies
    .filter(company => company.enabled)
    .map(company => ({
      name: company.name,
      shipper_id: company.id, // Use company ID as shipper reference
      marketplace_logistic_id: `logistics_${company.id}`, // Generate marketplace logistic ID
      is_active: company.enabled
    }))
}

/**
 * Transform certification/license file to API format
 * Only includes successfully uploaded certificate with file_id from server
 */
export function transformCertifications(
  licenseFileId: string | undefined,
  licenseFileUrl: string | undefined,
  licenseUploadStatus: string | undefined
): any[] {
  // Only include if file was successfully uploaded
  if (!licenseFileId || licenseUploadStatus !== 'success') return []

  return [{
    certification_id: `cert_${Date.now()}`, // Generate unique certification ID
    file_id: licenseFileId, // Use real server file ID
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // Default 1 year from now
    is_active: true
  }]
}

/**
 * Transform product videos to API format
 * Supports two modes:
 * - file_id mode: for server-uploaded video files
 * - file_url + file_hash mode: for URL-based videos (YouTube, Vimeo, etc.)
 */
export function transformVideos(
  productVideo: {
    type: 'file' | 'url';
    file_id?: string;
    file_hash?: string;
    url?: string;
    upload_status?: string;
  } | undefined
): any[] {
  if (!productVideo) return []

  if (productVideo.type === 'file' && productVideo.file_id && productVideo.upload_status === 'success') {
    // Uploaded video file - use file_id format
    return [{
      file_id: productVideo.file_id,
      is_primary: true,
      is_active: true
    }]
  } else if (productVideo.type === 'url' && productVideo.url && productVideo.url.trim()) {
    // External video URL - use file_url format with required file_hash
    return [{
      file_url: productVideo.url,
      file_hash: productVideo.file_hash || generateHashFromUrl(productVideo.url),
      is_primary: true,
      is_active: true
    }]
  }

  return []
}

/**
 * Transform category attributes to API format
 */
export function transformCategoryAttributes(
  categoryAttributes: Record<string, string> | undefined
): any[] {
  if (!categoryAttributes) return []
  
  return Object.entries(categoryAttributes).map(([name, value]) => ({
    name,
    value
  }))
}

/**
 * Build product object for API payload
 */
export function buildProductObject(
  productData: ProductData,
  isEditMode: boolean
): any {
  const { basicInfo, classification, salesInfo, shipping } = productData
  const product: any = {}
  
  // Core fields (always include in both modes)
  if (basicInfo.productName !== undefined) product.name = basicInfo.productName
  if (basicInfo.productSku !== undefined) product.sku = basicInfo.productSku || null
  if (classification.brandName !== undefined) product.brand_name = classification.brandName
  if (basicInfo.productDescription !== undefined) product.description = basicInfo.productDescription
  
  // Boolean fields (include if defined)
  if (shipping.isDangerous !== undefined) product.is_dangerous_product = shipping.isDangerous
  if (salesInfo.hasVariants !== undefined) product.is_product_has_variants = salesInfo.hasVariants
  
  // In create mode, set default values; in edit mode, only include if changed
  if (!isEditMode) {
    product.is_active = true
    product.is_bundle = false // TODO: Handle bundles if needed
  }

  // Category (include if provided)
  if (classification.categoryPath?.length > 0) {
    product.category = classification.categoryPath.map((cat, index) => ({
      id: cat.id,
      has_children: index < classification.categoryPath.length - 1 || cat.hasChildren
    }))
  }
  
  // Tags (include if provided)
  if (salesInfo.tags !== undefined) product.tags = salesInfo.tags
  if (salesInfo.promotionTags !== undefined) product.promotion_tags = salesInfo.promotionTags

  // Dimensions and weight (include if provided)
  if (shipping.packageDimensions?.height !== undefined || 
      shipping.packageDimensions?.length !== undefined || 
      shipping.packageDimensions?.width !== undefined) {
    product.dimension = {
      height: shipping.packageDimensions?.height || 0,
      length: shipping.packageDimensions?.length || 0,
      width: shipping.packageDimensions?.width || 0
    }
  }
  
  if (shipping.packageDimensions?.weight !== undefined) {
    product.weight = shipping.packageDimensions.weight
  }

  // Order and packing fields (include if provided)
  if (salesInfo.minOrder !== undefined) product.min_order = salesInfo.minOrder
  if (shipping.packingInstructions !== undefined) product.packing_instruction = shipping.packingInstructions

  // Pricing fields (include if provided)
  if (salesInfo.productPrice !== undefined) product.price = salesInfo.productPrice
  if (salesInfo.costPrice !== undefined) product.cost_price = salesInfo.costPrice
  if (salesInfo.salePrice !== undefined) product.sale_price = salesInfo.salePrice
  if (salesInfo.currency !== undefined) product.currency = salesInfo.currency

  // Stock fields (include if provided)
  if (salesInfo.totalStock !== undefined) product.stock = salesInfo.totalStock
  if (salesInfo.warehouseStock && Object.keys(salesInfo.warehouseStock).length > 0) {
    product.warehouse_stock = salesInfo.warehouseStock
  }

  // Pre-order settings (include if explicitly enabled)
  if (shipping.preOrderSettings?.enabled) {
    product.pre_order = {
      is_enabled: shipping.preOrderSettings.enabled,
      type: shipping.preOrderSettings.type || 'PRE_ORDER',
      ship_in_days: shipping.preOrderSettings.processingDays || 7
    }
  }

  return product
}